import React, { useEffect } from 'react'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import { useServiceContext } from '../../root'
import TableDateTimeCell from '../../components/shared/TableDateTimeCell'
import DueDateIndicator from '../../components/DueDateIndicator'
import { AclLevel, FsiType } from '../../constants/fsi'
import DocIcon from '../../components/shared/DocIcon'
import { Link } from 'react-router'
import DocState from '../../components/shared/DocState'
import { FsiGridItem } from '../browser/FsiGridItem'
import { DocumentTableMenu } from '../browser/DocumentTableMenu'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionConsumer
} from '../../components/rcm-table/TableMultiSelection'

interface SiteTableProps {
  totalElements: Promise<{ totalElements: number }> | number | null
}

const SiteTable: React.FC<SiteTableProps> = ({ totalElements }) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { filter } = context.service
  const { translate } = filter
  const isEtmfEnabled = context.service.init.getFeaturesInfo()?.etmfFeature?.show || false

  useEffect(() => {
    // Listen for table refresh events
    const handler = () => tableData.refreshTable()
    window.addEventListener('bctable:updateTable', handler)
    return () => {
      window.removeEventListener('bctable:updateTable', handler)
    }
  }, [tableData])

  return (
    <TableMultiSelectionWrapper
      isSelectTotalEnabled={true}
      texts={{
        selectAllItemsText: 'MULTISELECT_SELECT_ALL_IN_SECTION',
        allItemsSelectedText: 'MULTISELECT_ALL_N_ITEMS_IN_IN_SECTION_SELECTED'
      }}
      getTotalNumberFunction={async () => {
        if (typeof totalElements === 'number') return totalElements
        if (totalElements && typeof (totalElements as Promise<any>).then === 'function') {
          const data = await totalElements
          return data.totalElements
        }
        return 0
      }}
    >
      <RcmTable
        className="chita-table fsiTable"
        noResultsText={translate('NO_RESULTS_FILTERED')}
        disableOnInitLoad={true}
        simplePaginate={false}
        rowKey={'fsiId'}
        GridItemView={({ row }: { row: any }) => <FsiGridItem row={row?.fsiDetail} />}
        HeadRow={
          <TableHeadRow className="has-actions">
            <TableHeadCell className="clipped-cell with-checkbox" id="d8f8e116-1906-4012-9b42-be7997407f8b">
              <SelectAllRows />
            </TableHeadCell>
            <TableHeadCell sort={'name'}>{translate('NAME')}</TableHeadCell>
            <TableHeadCell sort={'artifact'}>{translate('ETMF_ARTIFACT')}</TableHeadCell>
            <TableHeadCell sort={'modifiedAt'} className="narrow">
              {translate('TABLE_TH_UPDATED_ON')}
            </TableHeadCell>
            <TableHeadCell className="narrow actions">{translate('ACTIONS')}</TableHeadCell>
          </TableHeadRow>
        }
        Row={({ row }: { row: any }) => {
          if (!row || !row.fsiDetail) return null
          return (
            <TableRow key={row.fsiId}>
              <td className="clipped-cell with-checkbox">
                <SelectRow row={row.fsiDetail} />
              </td>
              <td className="name clipped-cell with-thumb">
                <div className="d-flex align-left">
                  <DocIcon fsi={row.fsiDetail} />
                  <div className="ellipsis" title={row.name}>
                    {/* Folder link logic */}
                    {row.fsiDetail.type === FsiType.FOLDER &&
                      row.fsiDetail.acl !== AclLevel.BROWSE &&
                      row.fsiDetail.valid && <Link to={`/browser/${row.fsiDetail.id}`}>{row.name}</Link>}
                    {/* File link logic */}
                    {row.fsiDetail.type !== FsiType.PLACEHOLDER &&
                      row.fsiDetail.type !== FsiType.FOLDER &&
                      row.fsiDetail.acl !== AclLevel.BROWSE &&
                      row.fsiDetail.valid && <Link to={`/show-file/${row.fsiDetail.id}`}>{row.name}</Link>}
                    {/* Plain text fallback */}
                    {row.fsiDetail.type !== FsiType.PLACEHOLDER &&
                      (row.fsiDetail.acl === AclLevel.BROWSE || !row.fsiDetail.valid) && (
                        <span className="whitespaceprewrap" title={row.name}>
                          {row.name}
                        </span>
                      )}
                    {/* Placeholder fallback */}
                    {row.fsiDetail.type === FsiType.PLACEHOLDER && (
                      <span className="whitespaceprewrap" title={row.name}>
                        {row.name}
                        {isEtmfEnabled && (
                          <DueDateIndicator dueDateStatus={row.fsiDetail.dueDateStatus} translate={translate} />
                        )}
                      </span>
                    )}
                  </div>
                  <DocState fsi={row.fsiDetail} />
                </div>
              </td>
              <td className="clipped-cell">
                <div className="d-flex align-left">
                  <div className="ellipsis" title={row.artifact || translate('NOT_AVAILABLE')}>
                    {row.artifact || translate('NOT_AVAILABLE')}
                  </div>
                </div>
              </td>
              <td className="narrow">
                <TableDateTimeCell val={row.fsiDetail.updatedOn} includeTime={true} />
              </td>
              <td className="narrow updated">
                <div className="actions d-inline-block float-right">
                  <TableMultiSelectionConsumer>
                    {(tableMultiSelection: any) =>
                      tableMultiSelection ? (
                        <DocumentTableMenu
                          item={row.fsiDetail}
                          options={{
                            isPropertiesSearch: false,
                            parentFolderId: null,
                            isStagingUser: true,
                            ...(tableMultiSelection.multiSelectState?.selectedIds
                              ? {
                                  meta: {},
                                  selectedItems: tableData.entries.filter((i: any) =>
                                    tableMultiSelection.multiSelectState.selectedIds.includes(i.id)
                                  )
                                }
                              : null),
                            ...tableMultiSelection.multiSelectState,
                            deselectAll: tableMultiSelection.deselectAll
                          }}
                        />
                      ) : null
                    }
                  </TableMultiSelectionConsumer>
                </div>
              </td>
            </TableRow>
          )
        }}
      />
    </TableMultiSelectionWrapper>
  )
}

export default SiteTable
